import axios, { AxiosResponse, AxiosRequestConfig } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'

import { notifyProjectsScreenError } from './bugsnag'

// Report every failed API call to the monitoring tool. The projects-screen
// filter lives inside the notifier, so calls made anywhere else are dropped.
const reportFailedApiCall = (error: any) => {
  // The monitor must never interfere with the app: any failure while
  // reporting is swallowed and the original interceptor flow continues.
  try {
    // Cancelled/aborted requests are not failures — don't report them
    if (axios.isCancel(error)) return
    const response = error?.response
    const config = response?.config ?? error?.config
    const method = (config?.method ?? 'request').toUpperCase()
    const url = config?.url ?? 'unknown'
    const status = response?.status ?? 'network-error'
    const body = typeof response?.data === 'string' ? response.data.slice(0, 500) : response?.data

    notifyProjectsScreenError(new Error(`API ${method} ${url} failed with ${status}`), {
      method,
      url,
      status,
      body,
    })
  } catch {
    // intentionally silent
  }
}

const api = axios.create()
const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
api.defaults.headers.common['X-CSRF-Token'] = token
api.defaults.headers.common.Accept = 'application/json'

api.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data && response.headers['content-type'].includes('application/json')) {
      response.data = camelizeKeys(response.data)
    }

    return response
  },

  error => {
    // Every non-2xx lands here first: report it before any local handling
    // can swallow it, so the team always sees the exception in the monitor.
    reportFailedApiCall(error)

    // 401 Unauthorized - return response to allow handling without throw
    if (error.response?.status === 401) {
      return error.response
    }
    // 422 Unprocessable Entity - reject to trigger catch blocks
    // The response data contains validation errors that should be handled
    if (error.response?.status === 422) {
      return Promise.reject(error)
    }
    // All other errors - reject normally
    return Promise.reject(error)
  },
)

api.interceptors.request.use((config: AxiosRequestConfig) => {
  const newConfig = { ...config }
  if (newConfig.headers['Content-Type'] === 'multipart/form-data') {
    return newConfig
  }

  if (config.params) {
    newConfig.params = decamelizeKeys(config.params)
  }

  if (config.data) {
    newConfig.data = decamelizeKeys(config.data)
  }

  return newConfig
})

export default api
